import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { fmtMetric, fmtPct, gainClass } from "@/lib/format";
import { useMarketColors } from "@/lib/marketHours";

const COLUMNS = [["sp500", "nasdaq", "vix"], ["ust10y", "oil", "gold"], ["btc", "usdils"]];

// "10:42" for today, "Fri 16:00" for anything older (local time)
function fmtAsOf(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return "";
    const t = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    return d.toDateString() === new Date().toDateString() ? t : `${d.toLocaleDateString("en-US", { weekday: "short" })} ${t}`;
}

function MarketRow({ m, colored, onOpen }) {
    const cls = colored ? gainClass(m.change) : "text-foreground";
    return (
        <button onClick={() => onOpen(m.key)} data-testid={`market-${m.key}`}
            className="w-full grid grid-cols-[1fr_auto_auto_auto] items-baseline gap-3 px-2 py-1.5 rounded-md hover:bg-muted/60 transition-colors text-left">
            <span className="text-xs text-muted-foreground truncate">{m.label}{m.is_future ? " · Fut" : ""}</span>
            <span className="num text-[10px] text-muted-foreground/80" title="Time of last price update" data-testid={`market-${m.key}-time`}>{fmtAsOf(m.as_of)}</span>
            <span className={`num text-sm font-semibold ${cls}`} data-testid={`market-${m.key}-value`}>{fmtMetric(m.fmt, m.price)}</span>
            <span className={`num text-xs font-medium w-14 text-right ${cls}`} data-testid={`market-${m.key}-delta`}>{fmtPct(m.change_percent)}</span>
        </button>
    );
}

export function MarketLists({ data, onOpen }) {
    const colored = useMarketColors();
    const byKey = Object.fromEntries((data?.metrics || []).map((m) => [m.key, m]));
    const known = new Set(COLUMNS.flat());
    const cols = COLUMNS.map((c, i) => [...c.map((k) => byKey[k]).filter(Boolean),
        ...(i === 2 ? (data?.metrics || []).filter((m) => !known.has(m.key)) : [])]);
    return (
        <Card className="px-3 py-2.5" data-testid="market-lists">
            <div className="flex items-center gap-2 px-2 mb-1">
                <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Markets</span>
                {data?.premarket && <Badge variant="outline" className="text-[9px] border-primary/40 text-primary">Pre-market futures</Badge>}
                {!colored && <span className="text-[10px] text-muted-foreground" data-testid="market-closed-note">· market closed</span>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 md:divide-x divide-border">
                {cols.map((c, i) => (
                    <div key={i} className="md:px-2 first:md:pl-0 last:md:pr-0" data-testid={`market-col-${i + 1}`}>
                        {c.map((m) => <MarketRow key={m.key} m={m} colored={colored} onOpen={onOpen} />)}
                    </div>
                ))}
            </div>
        </Card>
    );
}
